import {SourceBrandIntroCard} from '@waka/compat/SourceBrandIntroCard';
import {sourceBrandIntroOfItem} from '@waka/compat/source-brand-intro';
import {SourceSlideLayer} from '@waka/compat/SourceSlide';
import {CinematicMotionCard} from '@waka/compat/CinematicMotionCard';
import {MaskRenderBoundary,CinematicMaskedImage} from '@waka/compat/CinematicMaskedMedia';
import {maskNeedsRendering} from '@waka/compat/cinematic-mask-effect';
import React, { useMemo } from 'react';
import { Img, useCurrentFrame, useVideoConfig } from 'remotion';
import { RequireCachedAsset } from '../../caching/require-cached-asset';
import { usePreferredLocalUrl } from '../../utils/find-asset-by-id';
import { useAssetFromItem } from '../../utils/use-context';
import { useCroppableLayer } from '../croppable-layer';
import { ZoomWrapper } from '../zoom-wrapper';
import {
  calculateAnimationsStyle,
  mergeAnimationStyle,
} from '../animation/calculate-animation';
import {
  calculateFadeInOpacity,
  calculateFadeOutOpacity,
} from '../video/calculate-fade';
import {
  calculateTemplateEffect,
  mergeTemplateStyle,
} from '../template/template-effects';
import { ImageItem } from './image-item-type';
import { getTrustedInlineSvgMarkup } from './get-trusted-inline-svg';

const ImageItemUnmemoized: React.FC<{
  item: ImageItem;
  cropBackground: boolean;
  zIndex?: number;
  disableBoundaryMediaPause?: boolean;
}> = ({ item, cropBackground, zIndex, disableBoundaryMediaPause }) => {
  if (item.type !== 'image') {
    throw new Error('Item is not an image');
  }

  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const asset = useAssetFromItem(item);

  const opacity = useMemo(() => {
    const inOpacity = calculateFadeInOpacity({
      fadeWindow:item.visualFadeWindow,
      currentFrame: frame,
      fadeInDurationInSeconds: item.fadeInDurationInSeconds,
      framesPerSecond: fps,
    });
    const outOpacity = calculateFadeOutOpacity({
      fadeWindow:item.visualFadeWindow,
      currentFrame: frame,
      fadeOutDurationInSeconds: item.fadeOutDurationInSeconds,
      framesPerSecond: fps,
      totalDurationInFrames: item.durationInFrames,
    });
    return inOpacity * outOpacity * item.opacity;
  }, [
    item.visualFadeWindow,
    item.fadeInDurationInSeconds,
    fps,
    frame,
    item.opacity,
    item.durationInFrames,
    item.fadeOutDurationInSeconds,
  ]);

  const { innerStyle, outerStyle } = useCroppableLayer({
    item,
    rotation: item.rotation,
    opacity,
    borderRadius: item.borderRadius,
    cropBackground,
    zIndex,
  });

  // Animation 스타일 계산
  const animationStyle = useMemo(() => {
    const hasAnimations = item.animations && item.animations.length > 0;
    const hasPositionKeyframes =
      item.positionKeyframes && item.positionKeyframes.length > 0;
    if (!hasAnimations && !hasPositionKeyframes) {
      return {};
    }
    return calculateAnimationsStyle({
      animations: item.animations ?? [],
      currentFrame: frame,
      framesPerSecond: fps,
      totalDurationInFrames: item.durationInFrames,
      positionKeyframes: item.positionKeyframes,
    });
  }, [
    item.animations,
    item.positionKeyframes,
    frame,
    fps,
    item.durationInFrames,
  ]);

  const templateEffect = useMemo(
    () =>
      calculateTemplateEffect({
        item,
        localFrame: frame,
        fps,
      }),
    [item, frame, fps],
  );

  const imageOuterStyle = useMemo(
    () => ({
      ...outerStyle,
      backgroundColor: item.backgroundColor,
      boxShadow: item.boxShadow,
    }),
    [outerStyle, item.backgroundColor, item.boxShadow],
  );

  const imageInnerStyle = useMemo(
    () => ({
      ...innerStyle,
      objectPosition: item.objectPosition,
    }),
    [innerStyle, item.objectPosition],
  );

  // outerStyle에 animation 적용
  const finalOuterStyle = useMemo(() => {
    const animatedOuterStyle = mergeAnimationStyle(
      imageOuterStyle,
      animationStyle,
    );
    if (!templateEffect) return animatedOuterStyle;
    return mergeTemplateStyle(animatedOuterStyle, templateEffect.outerStyle);
  }, [imageOuterStyle, animationStyle, templateEffect]);

  const finalInnerStyle = useMemo(() => {
    if (!templateEffect) return imageInnerStyle;
    return mergeTemplateStyle(imageInnerStyle, templateEffect.innerStyle);
  }, [imageInnerStyle, templateEffect]);

  const src = usePreferredLocalUrl(asset);

  // 신뢰된 SVG 에셋(우리 코드 생성 data URI)은 <Img>/에셋 캐싱 경로 대신 인라인 <svg>로 렌더한다.
  // 람다 렌더는 Chrome DOM 을 프레임마다 스크린샷하므로, 인라인 SVG 는 이미지 로드/URL 디코드/
  // blob 캐싱 단계 없이 네이티브로 페인트되어 프리뷰·람다에서 동일하게 그려진다. (data URI 를
  // <Img> 로 띄우면 람다의 캐싱 경로에서 깨져 export 가 실패했음.)
  // RequireCachedAsset 은 렌더 시 pass-through, 프리뷰에선 remoteUrl 없는 업로드중 에셋만 막으므로
  // data URI(remoteUrl 보유)에는 영향이 없어 우회해도 동작이 동일하다.
  // XSS 게이트 상세는 getTrustedInlineSvgMarkup 참조.
  const inlineSvgMarkup = useMemo(
    () => getTrustedInlineSvgMarkup(asset),
    [asset],
  );

  const zoomKeyframes = cropBackground ? undefined : item.zoomKeyframes;

  if(maskNeedsRendering(item.cinematicMasks))return (
    <div style={{...finalOuterStyle,backgroundColor:'#000'}}>
      <ZoomWrapper itemId={item.id} zoomKeyframes={zoomKeyframes} frame={frame}>
        <RequireCachedAsset asset={asset}>
          <MaskRenderBoundary key={JSON.stringify([item.cinematicMasks,asset.remoteUrl])}>
            <CinematicMaskedImage src={src} sourceUrl={asset.remoteUrl} masks={item.cinematicMasks} style={finalInnerStyle}/>
          </MaskRenderBoundary>
        </RequireCachedAsset>
      </ZoomWrapper>
    </div>
  );

  if (inlineSvgMarkup) {
    return (
      <div style={finalOuterStyle}>
        <ZoomWrapper
          itemId={item.id}
          zoomKeyframes={zoomKeyframes}
          frame={frame}
        >
          <div
            style={innerStyle}
            dangerouslySetInnerHTML={{ __html: inlineSvgMarkup }}
          />
        </ZoomWrapper>
      </div>
    );
  }

  return (
    <div style={finalOuterStyle}>
      <ZoomWrapper
        itemId={item.id}
        zoomKeyframes={zoomKeyframes}
        frame={frame}
      >
        <RequireCachedAsset asset={asset}>
          {item.sourceSlide?<MaskRenderBoundary label="장표"><SourceSlideLayer item={item} data={item.sourceSlide} src={src} frame={frame} fps={fps} style={finalInnerStyle}/></MaskRenderBoundary>:item.cinematicMotion?<MaskRenderBoundary label="모션 카드"><CinematicMotionCard shouldBuffer={frame>=0&&frame<item.durationInFrames&&!disableBoundaryMediaPause} data={item.cinematicMotion} src={src} sourceUrl={asset.remoteUrl} frame={frame} fps={fps} style={finalInnerStyle}/></MaskRenderBoundary>:<Img
            crossOrigin="anonymous"
            pauseWhenLoading={!disableBoundaryMediaPause}
            style={finalInnerStyle}
            src={src}
          />}
        </RequireCachedAsset>
      </ZoomWrapper>
      {templateEffect && templateEffect.overlayOpacity > 0 ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: '#FFFFFF',
            opacity: templateEffect.overlayOpacity,
            pointerEvents: 'none',
            mixBlendMode: 'screen',
          }}
        />
      ) : null}
      {templateEffect?.overlayStyle ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            ...templateEffect.overlayStyle,
          }}
        />
      ) : null}
    </div>
  );
};

const ProceduralOrImageLayer:React.FC<React.ComponentProps<typeof ImageItemUnmemoized>>=(props)=>{
  const frame=useCurrentFrame();
  const data=sourceBrandIntroOfItem(props.item);
  return data?<MaskRenderBoundary label="오프닝 카드"><SourceBrandIntroCard item={props.item} cropBackground={props.cropBackground} zIndex={props.zIndex} shouldBuffer={frame>=0&&frame<props.item.durationInFrames&&!props.disableBoundaryMediaPause}/></MaskRenderBoundary>:<ImageItemUnmemoized {...props}/>;
};
export const ImageLayer = React.memo(ProceduralOrImageLayer);
